import { useEffect, useRef } from 'react'

interface Wave {
  x: number
  y: number
  width: number
  height: number
  speed: number
  amplitude: number
}

interface Bubble {
  x: number
  y: number
  radius: number
  speed: number
}

interface Seagull {
  x: number
  y: number
  speed: number
}



export default function AnimatedBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationId = useRef<number | undefined>(undefined)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let width = window.innerWidth
    let height = window.innerHeight
    let time = 0

    // 创建海盗船图片
    const pirateShipImage = new Image()
    pirateShipImage.src = '/Pirate.png'

    // 初始化动画元素
    const waves: Wave[] = Array.from({ length: 8 }, (_, i) => ({
      x: 0,
      y: height * 0.5 + i * 15,
      width: width,
      height: 20,
      speed: 0.5 + i * 0.1,
      amplitude: 8 + i * 2
    }))

    const bubbles: Bubble[] = Array.from({ length: 25 }, () => ({
      x: Math.random() * width,
      y: height + Math.random() * height * 0.5,
      radius: Math.random() * 3 + 2,
      speed: Math.random() * 1 + 0.5
    }))

    const seagulls: Seagull[] = Array.from({ length: 6 }, () => ({
      x: Math.random() * width,
      y: height * 0.2 + Math.random() * height * 0.3,
      speed: Math.random() * 0.5 + 0.3
    }))

    const resize = () => {
      if (canvas) {
        width = window.innerWidth
        height = window.innerHeight
        canvas.width = width
        canvas.height = height
        
        // 重新初始化波浪位置
        waves.forEach((wave, i) => {
          wave.y = height * 0.5 + i * 15
          wave.width = width
        })
      }
    }

    resize()
    window.addEventListener('resize', resize)

    // 检查主题模式
    function checkTheme() {
      const savedTheme = localStorage.getItem('theme')
      if (savedTheme) {
        return savedTheme === 'dark'
      }
      const hour = new Date().getHours()
      return hour < 6 || hour >= 18
    }

    // 绘制真实海浪效果
    function drawWave(wave: Wave) {
      if (!ctx) return
      
      const isDarkMode = checkTheme()
      
      // 计算海浪的随机偏移，避免整齐的初始状态
      const randomOffset = Math.sin(wave.y * 0.1 + time * 0.1) * 50
      
      // 创建海浪路径
      ctx.beginPath()
      ctx.moveTo(0, height) // 从画布底部开始
      
            // 绘制海浪顶部曲线
      for (let x = 0; x <= width; x += 2) {
        const wave1 = Math.sin(x * 0.015 + time * wave.speed + randomOffset * 0.01) * wave.amplitude
        const wave2 = Math.sin(x * 0.025 + time * wave.speed * 1.2 + randomOffset * 0.02) * (wave.amplitude * 0.6)
        const wave3 = Math.sin(x * 0.035 + time * wave.speed * 0.8 + randomOffset * 0.03) * (wave.amplitude * 0.4)
        const y = wave.y + wave1 + wave2 + wave3
        
        ctx.lineTo(x, y)
      }
      
      // 完成海浪形状
      ctx.lineTo(width, height)
      ctx.lineTo(0, height)
      ctx.closePath()
      
      // 创建海浪渐变
      let waveGradient
      if (isDarkMode) {
        // 夜晚模式渐变
        waveGradient = ctx.createLinearGradient(0, wave.y - wave.amplitude, 0, height)
        waveGradient.addColorStop(0, 'rgba(30, 64, 175, 0.8)')   // 深蓝
        waveGradient.addColorStop(0.3, 'rgba(59, 130, 246, 0.6)') // 中蓝
        waveGradient.addColorStop(0.6, 'rgba(96, 165, 250, 0.4)') // 浅蓝
        waveGradient.addColorStop(1, 'rgba(147, 197, 253, 0.2)')  // 最浅蓝
      } else {
        // 白天模式渐变
        waveGradient = ctx.createLinearGradient(0, wave.y - wave.amplitude, 0, height)
        waveGradient.addColorStop(0, 'rgba(14, 165, 233, 0.9)')   // 天蓝
        waveGradient.addColorStop(0.3, 'rgba(59, 130, 246, 0.7)') // 中蓝
        waveGradient.addColorStop(0.6, 'rgba(96, 165, 250, 0.5)') // 浅蓝
        waveGradient.addColorStop(1, 'rgba(147, 197, 253, 0.3)')  // 最浅蓝
      }
      
      // 填充海浪
      ctx.fillStyle = waveGradient
      ctx.fill()
      
      // 绘制海浪顶部高光
      ctx.beginPath()
      ctx.moveTo(0, wave.y + wave.amplitude)
      
      for (let x = 0; x <= width; x += 2) {
        const wave1 = Math.sin(x * 0.015 + time * wave.speed + randomOffset * 0.01) * wave.amplitude
        const wave2 = Math.sin(x * 0.025 + time * wave.speed * 1.2 + randomOffset * 0.02) * (wave.amplitude * 0.6)
        const wave3 = Math.sin(x * 0.035 + time * wave.speed * 0.8 + randomOffset * 0.03) * (wave.amplitude * 0.4)
        const y = wave.y + wave1 + wave2 + wave3 - 8
        
        ctx.lineTo(x, y)
      }
      
      ctx.lineTo(width, wave.y + wave.amplitude)
      ctx.closePath()
      
      // 高光渐变
      const highlightGradient = ctx.createLinearGradient(0, wave.y - wave.amplitude, 0, wave.y + wave.amplitude)
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)')
      highlightGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.2)')
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
      
      ctx.fillStyle = highlightGradient
      ctx.fill()
      
      // 绘制海浪纹理线条
      ctx.strokeStyle = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.15)'
      ctx.lineWidth = 1
      
      for (let i = 0; i < 3; i++) {
        ctx.beginPath()
        ctx.moveTo(0, wave.y + wave.amplitude + 20 + i * 15)
        
        for (let x = 0; x <= width; x += 20) {
          const wave1 = Math.sin(x * 0.015 + time * wave.speed + randomOffset * 0.01) * (wave.amplitude * 0.3)
          const wave2 = Math.sin(x * 0.025 + time * wave.speed * 1.2 + randomOffset * 0.02) * (wave.amplitude * 0.2)
          const y = wave.y + wave.amplitude + 20 + i * 15 + wave1 + wave2
          
          ctx.lineTo(x, y)
        }
        
        ctx.stroke()
      }
      
      // 绘制海浪阴影
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'
      ctx.beginPath()
      ctx.moveTo(0, wave.y + wave.amplitude + 10)
      
      for (let x = 0; x <= width; x += 2) {
        const wave1 = Math.sin(x * 0.015 + time * wave.speed + randomOffset * 0.01) * wave.amplitude
        const wave2 = Math.sin(x * 0.025 + time * wave.speed * 1.2 + randomOffset * 0.02) * (wave.amplitude * 0.6)
        const wave3 = Math.sin(x * 0.035 + time * wave.speed * 0.8 + randomOffset * 0.03) * (wave.amplitude * 0.4)
        const y = wave.y + wave1 + wave2 + wave3 + 10
        
        ctx.lineTo(x, y)
      }
      
      ctx.lineTo(width, wave.y + wave.amplitude + 10)
      ctx.closePath()
      ctx.fill()
    }

    // 绘制海天交界处的海浪
    function drawHorizonWaves() {
      if (!ctx) return
      
      const isDarkMode = checkTheme()
      const horizonY = height * 0.6
      
      // 绘制海天交界处的渐变
      const horizonGradient = ctx.createLinearGradient(0, horizonY - 50, 0, horizonY + 50)
      if (isDarkMode) {
        horizonGradient.addColorStop(0, 'rgba(30, 64, 175, 0.3)')   // 深蓝
        horizonGradient.addColorStop(0.5, 'rgba(59, 130, 246, 0.2)') // 中蓝
        horizonGradient.addColorStop(1, 'rgba(96, 165, 250, 0.1)')   // 浅蓝
      } else {
        horizonGradient.addColorStop(0, 'rgba(14, 165, 233, 0.4)')   // 天蓝
        horizonGradient.addColorStop(0.5, 'rgba(59, 130, 246, 0.3)') // 中蓝
        horizonGradient.addColorStop(1, 'rgba(96, 165, 250, 0.2)')   // 浅蓝
      }
      
      ctx.fillStyle = horizonGradient
      ctx.fillRect(0, horizonY - 50, width, 100)
      
      // 绘制海天交界处的海浪
      ctx.strokeStyle = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      
      for (let i = 0; i < 3; i++) {
        ctx.beginPath()
        ctx.moveTo(0, horizonY + i * 15)
        
        for (let x = 0; x <= width; x += 3) {
          const wave1 = Math.sin(x * 0.02 + time * (0.3 + i * 0.2)) * (8 + i * 3)
          const wave2 = Math.sin(x * 0.03 + time * (0.5 + i * 0.1)) * (5 + i * 2)
          const y = horizonY + i * 15 + wave1 + wave2
          
          ctx.lineTo(x, y)
        }
        
        ctx.stroke()
      }
    }

    // 绘制海盗船图片
    function drawPirateShip() {
      if (!ctx || !pirateShipImage.complete) return
      
      const shipX = width * 0.5
      const shipY = height * 0.55
      const shipWidth = 200
      const shipHeight = shipWidth * (pirateShipImage.height / pirateShipImage.width)
      
      // 添加轻微的摇摆效果 - 随海浪摇晃
      const sway = Math.sin(time * 0.3) * 3
      const verticalSway = Math.sin(time * 0.2) * 6
      const horizontalSway = Math.cos(time * 0.25) * 4
      
      ctx.save()
      ctx.translate(shipX + horizontalSway, shipY + verticalSway)
      ctx.rotate(sway * 0.008)
      ctx.globalAlpha = 0.9
      ctx.drawImage(
        pirateShipImage,
        -shipWidth / 2,
        -shipHeight / 2,
        shipWidth,
        shipHeight
      )
      ctx.restore()
    }

    // 绘制气泡
    function drawBubble(bubble: Bubble) {
      if (!ctx) return
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'
      ctx.beginPath()
      ctx.arc(bubble.x, bubble.y, bubble.radius, 0, 2 * Math.PI)
      ctx.fill()
      
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(bubble.x, bubble.y, bubble.radius, 0, 2 * Math.PI)
      ctx.stroke()
      
      // 气泡高光
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
      ctx.beginPath()
      ctx.arc(bubble.x - bubble.radius * 0.3, bubble.y - bubble.radius * 0.3, bubble.radius * 0.4, 0, 2 * Math.PI)
      ctx.fill()
    }

    // 绘制海鸥
    function drawSeagull(seagull: Seagull) {
      if (!ctx) return
      
      ctx.fillStyle = checkTheme() ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'
      ctx.beginPath()
      
      const wingSpan = 20
      const wingFlap = Math.sin(time * 4 + seagull.x * 0.01) * 5
      
      // 海鸥身体
      ctx.ellipse(seagull.x, seagull.y, 3, 1, 0, 0, 2 * Math.PI)
      
      // 海鸥翅膀
      ctx.beginPath()
      ctx.moveTo(seagull.x - 5, seagull.y)
      ctx.quadraticCurveTo(seagull.x - wingSpan, seagull.y - wingFlap, seagull.x - wingSpan, seagull.y - 10)
      ctx.quadraticCurveTo(seagull.x - wingSpan, seagull.y - 15, seagull.x - 5, seagull.y)
      ctx.fill()
      
      ctx.beginPath()
      ctx.moveTo(seagull.x + 5, seagull.y)
      ctx.quadraticCurveTo(seagull.x + wingSpan, seagull.y - wingFlap, seagull.x + wingSpan, seagull.y - 10)
      ctx.quadraticCurveTo(seagull.x + wingSpan, seagull.y - 15, seagull.x + 5, seagull.y)
      ctx.fill()
    }

    // 绘制星星
    function drawStars() {
      if (!ctx) return
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
      for (let i = 0; i < 50; i++) {
        const x = (Math.sin(i * 234.2 + time) * 1000) % width
        const y = (Math.cos(i * 123.4 + time) * 1000) % (height * 0.6)
        const size = Math.sin(i + time) * 0.5 + 0.5
        
        ctx.beginPath()
        ctx.arc(x, y, size, 0, 2 * Math.PI)
        ctx.fill()
      }
    }

    // 绘制太阳
    function drawSun() {
      if (!ctx) return
      
      const sunX = width * 0.15  // 改为左上角位置
      const sunY = height * 0.15
      const sunRadius = 35
      
      // 太阳光晕
      const sunGlow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunRadius * 2)
      sunGlow.addColorStop(0, 'rgba(255, 255, 0, 0.8)')
      sunGlow.addColorStop(0.5, 'rgba(255, 255, 0, 0.4)')
      sunGlow.addColorStop(1, 'rgba(255, 255, 0, 0)')
      
      ctx.fillStyle = sunGlow
      ctx.beginPath()
      ctx.arc(sunX, sunY, sunRadius * 2, 0, 2 * Math.PI)
      ctx.fill()
      
      // 太阳主体
      ctx.fillStyle = '#fbbf24'
      ctx.beginPath()
      ctx.arc(sunX, sunY, sunRadius, 0, 2 * Math.PI)
      ctx.fill()
      
      // 太阳高光
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
      ctx.beginPath()
      ctx.arc(sunX - 8, sunY - 8, 6, 0, 2 * Math.PI)
      ctx.fill()

      // 太阳光线
      ctx.strokeStyle = 'rgba(255, 255, 0, 0.6)'
      ctx.lineWidth = 2
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * 2 * Math.PI
        const startX = sunX + Math.cos(angle) * sunRadius
        const startY = sunY + Math.sin(angle) * sunRadius
        const endX = sunX + Math.cos(angle) * (sunRadius + 18)
        const endY = sunY + Math.sin(angle) * (sunRadius + 18)
        
        ctx.beginPath()
        ctx.moveTo(startX, startY)
        ctx.lineTo(endX, endY)
        ctx.stroke()
      }
    }

    // 绘制月亮
    function drawMoon() {
      if (!ctx) return
      
      const moonX = width * 0.8
      const moonY = height * 0.2
      const moonRadius = 30
      
      // 月亮光晕
      const moonGlow = ctx.createRadialGradient(moonX, moonY, 0, moonX, moonY, moonRadius * 1.5)
      moonGlow.addColorStop(0, 'rgba(255, 255, 255, 0.6)')
      moonGlow.addColorStop(0.7, 'rgba(255, 255, 255, 0.2)')
      moonGlow.addColorStop(1, 'rgba(255, 255, 255, 0)')
      
      ctx.fillStyle = moonGlow
      ctx.beginPath()
      ctx.arc(moonX, moonY, moonRadius * 1.5, 0, 2 * Math.PI)
      ctx.fill()
      
      // 月亮主体
      ctx.fillStyle = '#f8fafc'
      ctx.beginPath()
      ctx.arc(moonX, moonY, moonRadius, 0, 2 * Math.PI)
      ctx.fill()

      // 月亮环形山
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'
      ctx.beginPath()
      ctx.arc(moonX - moonRadius * 0.3, moonY - moonRadius * 0.2, moonRadius * 0.2, 0, 2 * Math.PI)
      ctx.fill()
      
      ctx.beginPath()
      ctx.arc(moonX + moonRadius * 0.2, moonY + moonRadius * 0.3, moonRadius * 0.15, 0, 2 * Math.PI)
      ctx.fill()
    }

    // 主绘制函数
    function draw() {
      if (!ctx) return
      
      ctx.clearRect(0, 0, width, height)
      
      const isDarkMode = checkTheme()
      
      if (isDarkMode) {
        // 暗色模式 - 夜空
        const nightGradient = ctx.createLinearGradient(0, 0, 0, height * 0.6)
        nightGradient.addColorStop(0, '#0f172a')  // 深蓝夜空
        nightGradient.addColorStop(0.3, '#1e293b') // 中蓝夜空
        nightGradient.addColorStop(0.6, '#334155') // 浅蓝夜空
        nightGradient.addColorStop(1, '#475569')   // 灰蓝夜空
        ctx.fillStyle = nightGradient
        ctx.fillRect(0, 0, width, height * 0.6)
        
        // 绘制星星
        drawStars()
        
        // 绘制月亮
        drawMoon()
        
        // 暗色海洋
        const darkOceanGradient = ctx.createLinearGradient(0, height * 0.6, 0, height)
        darkOceanGradient.addColorStop(0, '#1e40af')  // 深蓝海洋
        darkOceanGradient.addColorStop(0.3, '#1e3a8a') // 更深蓝
        darkOceanGradient.addColorStop(0.6, '#1e293b') // 深灰蓝
        darkOceanGradient.addColorStop(1, '#0f172a')   // 深蓝
        ctx.fillStyle = darkOceanGradient
        ctx.fillRect(0, height * 0.6, width, height * 0.4)
      } else {
        // 明亮模式 - 天空
        const skyGradient = ctx.createLinearGradient(0, 0, 0, height * 0.6)
        skyGradient.addColorStop(0, '#bae6fd')  // 天空蓝
        skyGradient.addColorStop(0.3, '#e0f2fe') // 浅海洋蓝
        skyGradient.addColorStop(0.6, '#f0f9ff') // 海面白
        skyGradient.addColorStop(1, '#f8fafc')   // 海泡沫白
        ctx.fillStyle = skyGradient
        ctx.fillRect(0, 0, width, height * 0.6)
        
        // 绘制太阳
        drawSun()
        
        // 明亮海洋
        const oceanGradient = ctx.createLinearGradient(0, height * 0.6, 0, height)
        oceanGradient.addColorStop(0, '#0ea5e9')  // 海洋蓝
        oceanGradient.addColorStop(0.3, '#0284c7') // 深海洋蓝
        oceanGradient.addColorStop(0.6, '#0369a1') // 深海蓝
        oceanGradient.addColorStop(1, '#0c4a6e')   // 深蓝
        ctx.fillStyle = oceanGradient
        ctx.fillRect(0, height * 0.6, width, height * 0.4)
      }
      
      // 绘制海天交界处的海浪
      drawHorizonWaves()
      
      // 绘制波浪
      waves.forEach(drawWave)
      
      // 更新和绘制气泡
      bubbles.forEach(bubble => {
        bubble.y -= bubble.speed
        if (bubble.y < -bubble.radius) {
          bubble.y = height + bubble.radius
          bubble.x = Math.random() * width
        }
        drawBubble(bubble)
      })
      
      // 更新和绘制海鸥
      seagulls.forEach(seagull => {
        seagull.x += seagull.speed
        if (seagull.x > width + 100) {
          seagull.x = Math.random() * width
          seagull.y = height * 0.2 + Math.random() * height * 0.3
        }
        drawSeagull(seagull)
      })

      // 绘制海盗船
      drawPirateShip()

      // 添加一些云朵（仅在明亮模式）
      if (!isDarkMode) {
        // 绘制多组不同大小和位置的云朵
        const clouds = [
          { x: (time * 0.05 + 100) % (width + 150) - 75, y: 80, size: 25, opacity: 0.3 },
          { x: (time * 0.03 + 300) % (width + 200) - 100, y: 120, size: 35, opacity: 0.4 },
          { x: (time * 0.07 + 500) % (width + 180) - 90, y: 60, size: 20, opacity: 0.25 },
          { x: (time * 0.04 + 700) % (width + 220) - 110, y: 100, size: 30, opacity: 0.35 },
          { x: (time * 0.06 + 900) % (width + 160) - 80, y: 140, size: 28, opacity: 0.3 },
          { x: (time * 0.02 + 1100) % (width + 240) - 120, y: 70, size: 22, opacity: 0.2 }
        ]
        
        clouds.forEach(cloud => {
          ctx.fillStyle = `rgba(255, 255, 255, ${cloud.opacity})`
          ctx.beginPath()
          // 绘制云朵的主体部分
          ctx.arc(cloud.x, cloud.y, cloud.size, 0, 2 * Math.PI)
          ctx.arc(cloud.x + cloud.size * 0.8, cloud.y, cloud.size * 0.7, 0, 2 * Math.PI)
          ctx.arc(cloud.x + cloud.size * 1.6, cloud.y, cloud.size * 0.6, 0, 2 * Math.PI)
          ctx.arc(cloud.x - cloud.size * 0.3, cloud.y, cloud.size * 0.5, 0, 2 * Math.PI)
          ctx.arc(cloud.x + cloud.size * 0.4, cloud.y - cloud.size * 0.3, cloud.size * 0.4, 0, 2 * Math.PI)
          ctx.fill()
        })
      }
      
      time += 0.02
      animationId.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      if (animationId.current) {
        cancelAnimationFrame(animationId.current)
      }
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: -1 }}
    />
  )
}